/**
 * File Category Section Component
 * Phase 1: Structured File Attachment System
 * 
 * Displays files grouped by category with collapsible sections
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Image, Mic, HelpCircle, File, Download, Eye, Trash2, Edit, Send, Star, CheckCircle } from 'lucide-react';
import { ClassificationBadge } from './fileClassification';
import { FILE_CATEGORIES } from './FileUploadModal';
import './post-completion-upload.css';

// Map category keys to display info
const CATEGORY_DISPLAY = {
  prior_search_package: {
    label: 'Prior Search Package',
    icon: '🗂️',
    bgGradient: 'from-violet-600 to-indigo-700',
    borderColor: 'border-violet-200',
    bgColor: 'bg-violet-50',
    badge: 'Internal only — not delivered to client',
  },
  // MLS-REPORT-FILES-001: generated Municipal Lien Search Report (MLS component completion)
  mls_lien_report: {
    label: 'MLS Lien Search Report',
    icon: '📄',
    bgGradient: 'from-[#13294b] to-[#1d4ed8]',
    borderColor: 'border-blue-200',
    bgColor: 'bg-blue-50',
  },
  // MLS-MAILAWAY-LETTER-001: generated Code Enforcement / Building Permit request letters
  mls_request_letter: {
    label: 'MLS Request Letters',
    icon: '✉️',
    bgGradient: 'from-[#13294b] to-[#334155]',
    borderColor: 'border-slate-200',
    bgColor: 'bg-slate-50',
  },
  final_search_report: {
    label: 'Final Search Report',
    icon: '📄',
    bgGradient: 'from-blue-700 to-blue-800',
    borderColor: 'border-blue-200',
    bgColor: 'bg-blue-50',
  },
  final_document: {
    label: 'Final Documents',
    icon: '📄',
    bgGradient: 'from-red-500 to-red-600',
    borderColor: 'border-red-200',
    bgColor: 'bg-red-50',
  },
  supporting_document: {
    label: 'Supporting Documents',
    icon: '📑',
    bgGradient: 'from-blue-500 to-blue-600',
    borderColor: 'border-blue-200',
    bgColor: 'bg-blue-50',
  },
  internal_note: {
    label: 'Internal Notes',
    icon: '📝',
    bgGradient: 'from-gray-500 to-gray-600',
    borderColor: 'border-gray-200',
    bgColor: 'bg-gray-50',
  },
  image: {
    label: 'Images / Screenshots',
    icon: '🖼',
    bgGradient: 'from-green-500 to-green-600',
    borderColor: 'border-green-200',
    bgColor: 'bg-green-50',
  },
  audio: {
    label: 'Audio Recordings',
    icon: '🎧',
    bgGradient: 'from-purple-500 to-purple-600',
    borderColor: 'border-purple-200',
    bgColor: 'bg-purple-50',
  },
  clarification_response: {
    label: 'Clarification Documents',
    icon: '❓',
    bgGradient: 'from-orange-500 to-orange-600',
    borderColor: 'border-orange-200',
    bgColor: 'bg-orange-50',
  },
  other: {
    label: 'Other Files',
    icon: '📎',
    bgGradient: 'from-slate-500 to-slate-600',
    borderColor: 'border-slate-200',
    bgColor: 'bg-slate-50',
  },
  // Legacy categories for backward compatibility
  general: {
    label: 'General Files',
    icon: '📎',
    bgGradient: 'from-slate-500 to-slate-600',
    borderColor: 'border-slate-200',
    bgColor: 'bg-slate-50',
  },
  tax_search_document: {
    label: 'Tax Search Documents',
    icon: '📄',
    bgGradient: 'from-[#4c2aff] to-[#784bff]',
    borderColor: 'border-purple-200',
    bgColor: 'bg-purple-50',
  },
  tax_email_response: {
    label: 'Tax Collector Email Response',
    icon: '📨',
    bgGradient: 'from-teal-500 to-teal-600',
    borderColor: 'border-teal-200',
    bgColor: 'bg-teal-50',
  },
  fax_evidence: {
    label: 'Fax Evidence',
    icon: '📠',
    bgGradient: 'from-amber-500 to-amber-600',
    borderColor: 'border-amber-200',
    bgColor: 'bg-amber-50',
  },
  email_evidence: {
    label: 'Email Evidence',
    icon: '📧',
    bgGradient: 'from-cyan-500 to-cyan-600',
    borderColor: 'border-cyan-200',
    bgColor: 'bg-cyan-50',
  },
  mail_evidence: {
    label: 'Mail Evidence',
    icon: '📬',
    bgGradient: 'from-teal-500 to-teal-600',
    borderColor: 'border-teal-200',
    bgColor: 'bg-teal-50',
  },
};

// Group files by category
export const groupFilesByCategory = (files) => {
  const groups = {};
  
  files.forEach(file => {
    // Determine the category
    let category = file.file_category || 'other';
    
    // Handle legacy/special file types
    if (file.is_tax_search_document) {
      category = 'tax_search_document';
    } else if (file.file_type === 'clarification_response' || file.file_category === 'clarification_response') {
      category = 'clarification_response';
    } else if (file.file_type === 'mail_away_response') {
      category = 'mail_away_response'; // Keep mail-away responses in their own category
    } else if (file.file_type === 'mlc_letter') {
      // P1 FIX: MLC letters should NOT appear in final_document section
      // They have their own dedicated "MLC Letter" section in FilesTab.jsx
      // Setting category to 'mlc_letter' ensures they are excluded from Final Documents
      category = 'mlc_letter';
    } else if (category === 'general') {
      category = 'other'; // Map general to other
    }
    
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(file);
  });
  
  return groups;
};

// Get category display order
// NOTE: 'mlc_letter', 'mail_away_response', and 'clarification_response' are intentionally excluded
// because they have their own dedicated sections in FilesTab.jsx
const CATEGORY_ORDER = [
  'prior_search_package',
  'mls_lien_report',
  'mls_request_letter',
  'final_search_report',
  'final_document',
  'supporting_document',
  'fax_evidence',
  'tax_email_response',
  'email_evidence',
  'mail_evidence',
  'image',
  'audio',
  'internal_note',
  'tax_search_document',
  'other',
  'general',
];

const FileCategorySection = ({ 
  categoryKey, 
  files, 
  onDownload,
  onPreview,
  onRename,
  onDelete,
  onSend,
  canEdit = true,
  canDelete = true,
  canSend = true,
  defaultExpanded = true,
  formatFileSize,
  getRelativeTime,
  showClassification = false,   // MLS-DOC-CLASS-001: dual MLS + Tax orders only (read-only badge)
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  const display = CATEGORY_DISPLAY[categoryKey] || CATEGORY_DISPLAY.other;
  
  if (!files || files.length === 0) return null;

  const getFileTypeIcon = (file) => {
    const extension = file.filename?.split('.').pop()?.toLowerCase();
    const contentType = file.content_type || file.file_type || '';
    
    if (contentType.includes('audio') || ['mp3', 'wav'].includes(extension)) {
      return <Mic className="w-5 h-5 text-purple-600" />;
    }
    if (contentType.includes('image') || ['png', 'jpg', 'jpeg', 'gif'].includes(extension)) {
      return <Image className="w-5 h-5 text-green-600" />;
    }
    if (extension === 'pdf') {
      return <FileText className="w-5 h-5 text-red-600" />;
    }
    if (['doc', 'docx'].includes(extension)) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    if (['xls', 'xlsx', 'csv'].includes(extension)) {
      return <FileText className="w-5 h-5 text-emerald-600" />;
    }
    return <File className="w-5 h-5 text-slate-600" />;
  };

  return (
    <div className={`mb-4 border ${display.borderColor} rounded-xl overflow-hidden`}>
      {/* Section Header - Clickable to expand/collapse */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r ${display.bgGradient} text-white hover:opacity-95 transition-opacity`}
        data-testid={`category-section-${categoryKey}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{display.icon}</span>
          <span className="font-semibold">{display.label}</span>
          <span className="px-2 py-0.5 bg-white/20 rounded-full text-sm">
            {files.length} file{files.length !== 1 ? 's' : ''}
          </span>
          {display.badge && (
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/90 text-violet-800" data-testid={`category-badge-${categoryKey}`}>
              {display.badge}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5" />
        ) : (
          <ChevronRight className="w-5 h-5" />
        )}
      </button>
      
      {/* Section Content */}
      {isExpanded && (
        <div className={`p-4 ${display.bgColor}`}>
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="pc-row-in flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-shadow"
                data-testid={`file-item-${file.id}`}
              >
                {/* File Icon */}
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {getFileTypeIcon(file)}
                </div>
                
                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span 
                      className="text-sm font-medium text-slate-900 truncate cursor-pointer hover:text-purple-600"
                      onClick={() => onPreview?.(file)}
                      title={file.filename}
                    >
                      {file.filename}
                    </span>
                    {file.is_final_search_report && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700" data-testid={`final-badge-${file.id}`}>
                        <CheckCircle className="w-3 h-3" /> Final
                      </span>
                    )}
                    {showClassification && !file.is_final_pdf && file.file_category !== 'tax_certificate_package' && (
                      <ClassificationBadge file={file} />
                    )}
                    {file.qc_added && (
                      <span title={file.qc_note || undefined}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${file.qc_deliverable ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}
                        data-testid={`qc-doc-badge-${file.id}`} data-qc-deliverable={file.qc_deliverable ? 'true' : 'false'}>
                        {file.qc_deliverable ? 'QC · Final Package' : 'QC · Internal Only'}
                      </span>
                    )}
                    {file.post_completion && (
                      <span title={`${file.post_completion_note || ''}${file.post_completion_by_name ? ` — ${file.post_completion_by_name} (${(file.post_completion_by_role || '').replace(/_/g, ' ')})` : ''}`}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold bg-amber-50 text-amber-800 border border-amber-200"
                        data-testid={`post-completion-badge-${file.id}`}>
                        Added after completion
                      </span>
                    )}
                    {file.portal_handoff && (
                      <span title={`Confirmed by ${file.portal_handoff.confirmed_by_name} · ${file.portal_handoff.confirmed_at}`}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold bg-purple-50 text-purple-700 border border-purple-200"
                        data-testid={`qc-doc-portal-confirmed-${file.id}`}>
                        Uploaded to Client Portal ✓
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                    {file.uploaded_by && <span>{file.uploaded_by}{file.qc_added ? <span className="ml-1 text-blue-700 font-medium" data-testid={`qc-uploaded-by-${file.id}`}>(Uploaded by QC)</span> : null}</span>}
                    {file.uploaded_by && file.uploaded_at && <span>•</span>}
                    {file.uploaded_at && <span>{getRelativeTime?.(file.uploaded_at) || file.uploaded_at}</span>}
                    {(file.file_size || file.size) && (
                      <>
                        <span>•</span>
                        <span>{formatFileSize?.(file.file_size || file.size) || file.file_size}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {onPreview && (
                    <button
                      onClick={() => onPreview(file)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4 text-slate-500" />
                    </button>
                  )}
                  {onDownload && (
                    <button
                      onClick={() => onDownload(file)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4 text-slate-500" />
                    </button>
                  )}
                  {canSend && onSend && (
                    <button
                      onClick={() => onSend(file)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Send"
                    >
                      <Send className="w-4 h-4 text-slate-500" />
                    </button>
                  )}
                  {canEdit && onRename && !file.is_final_search_report && (
                    <button
                      onClick={() => onRename(file)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Rename"
                    >
                      <Edit className="w-4 h-4 text-slate-500" />
                    </button>
                  )}
                  {canDelete && onDelete && !file.is_final_search_report && (
                    <button
                      onClick={() => onDelete(file)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Component to render all category sections
export const FileCategorySections = ({ 
  files, 
  ...fileActionProps 
}) => {
  const groupedFiles = groupFilesByCategory(files);
  
  // Sort categories by predefined order
  const sortedCategories = CATEGORY_ORDER.filter(cat => groupedFiles[cat]?.length > 0);
  
  if (sortedCategories.length === 0) {
    return (
      <div className="pc-empty" data-testid="files-empty-state">
        <img src="/illustrations/empty-files.png" alt="" aria-hidden="true" />
        <p className="mt-1.5 text-base font-bold text-slate-900">No files uploaded yet</p>
        <p className="text-[13px] text-slate-500 mt-0.5 max-w-xs">Upload supporting documents, notes to keep everything in one place.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedCategories.map(categoryKey => (
        <FileCategorySection
          key={categoryKey}
          categoryKey={categoryKey}
          files={groupedFiles[categoryKey]}
          defaultExpanded={categoryKey === 'final_document' || sortedCategories.length <= 3}
          {...fileActionProps}
        />
      ))}
    </div>
  );
};

export default FileCategorySection;
